import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { X as CloseIcon } from "react-feather";

import {
  ADMIN_EVENT_TIMEZONE_SUPPLY,
  ADMIN_TIMEZONE_REGION_MEMBERS,
} from "../../gql/queries/eventInsights";
import { BAND_HOURS, BAND_LABEL, bandOf, fmtInt, localDateTime, rgba, TZ_BANDS } from "./helpers";
import RegionOutreach from "./RegionOutreach";
import type { EventTimezoneSupply, TimezoneRegionMembers, TzEvent, TzRegion } from "./types";

const GREEN: [number, number, number] = [0, 88, 61];
const HOST_LABEL: Record<string, string> = {
  coach: "Coach",
  big_sister: "Big Sister",
  cxt: "CXT",
  member: "Member",
};

type Drill =
  | { kind: "events"; region: TzRegion; band: string }
  | { kind: "members"; region: TzRegion }
  | null;

const EventTimezoneSupplyPanel: React.FC = () => {
  const [days, setDays] = useState(7);
  const [activeHosts, setActiveHosts] = useState<Set<string> | null>(null);
  const [drill, setDrill] = useState<Drill>(null);

  const { data, loading, error } = useQuery(ADMIN_EVENT_TIMEZONE_SUPPLY, {
    variables: { days },
    fetchPolicy: "cache-and-network",
  });
  const supply: EventTimezoneSupply | undefined = data?.adminEventTimezoneSupply;

  // Default: every host type except member-hosted.
  const hosts = useMemo(() => {
    if (activeHosts) {
      return activeHosts;
    }
    return new Set((supply?.hostTypes ?? []).map((h) => h.key).filter((k) => k !== "member"));
  }, [activeHosts, supply]);

  const shownEvents = useMemo(
    () => (supply?.events ?? []).filter((e) => hosts.has(e.hostType)),
    [supply, hosts],
  );

  if (loading && !supply) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">Couldn’t load timezone supply. {error.message}</Alert>;
  }
  if (!supply || supply.regions.length === 0) {
    return <Alert severity="info">No members with a known time zone yet.</Alert>;
  }

  const toggleHost = (key: string): void => {
    const next = new Set(hosts);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setActiveHosts(next);
  };

  // counts[offset][band]
  const counts = new Map<number, Record<string, number>>();
  let max = 0;
  supply.regions.forEach((r) => {
    const c: Record<string, number> = { morning: 0, afternoon: 0, evening: 0, night: 0 };
    shownEvents.forEach((e) => {
      c[bandOf(e.utcMin, r.offset)] += 1;
    });
    counts.set(r.offset, c);
    TZ_BANDS.forEach((b) => {
      max = Math.max(max, c[b]);
    });
  });

  const gridTemplate = `230px repeat(4, minmax(72px, 1fr)) 90px`;
  const headSx = {
    fontSize: 11,
    fontWeight: 600,
    color: "text.secondary",
    px: 1,
    py: 0.75,
    borderBottom: "1px solid",
    borderColor: "divider",
  } as const;

  const cells: React.ReactNode[] = [];
  cells.push(
    <Box key="corner" sx={{ ...headSx, fontWeight: 700 }}>
      Region ╲ Local time
    </Box>,
  );
  TZ_BANDS.forEach((b) => {
    cells.push(
      <Box key={`h-${b}`} sx={{ ...headSx, textAlign: "center" }}>
        {BAND_LABEL[b]}
        <Box component="span" sx={{ display: "block", fontSize: 9, color: "text.disabled" }}>
          {BAND_HOURS[b]} local
        </Box>
      </Box>,
    );
  });
  cells.push(
    <Box key="h-members" sx={{ ...headSx, textAlign: "right" }}>
      Members
    </Box>,
  );

  supply.regions.forEach((r) => {
    cells.push(
      <Tooltip key={`l-${r.offset}`} title={r.label} arrow>
        <Box
          sx={{
            fontSize: 12,
            px: 1,
            py: 0.75,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            borderBottom: "1px solid",
            borderColor: "action.hover",
          }}
        >
          {r.label}
        </Box>
      </Tooltip>,
    );
    const c = counts.get(r.offset) as Record<string, number>;
    TZ_BANDS.forEach((b) => {
      const v = c[b];
      const alpha = max ? 0.08 + 0.85 * (v / max) : 0;
      cells.push(
        <Tooltip
          key={`c-${r.offset}-${b}`}
          title={`${r.label} · ${BAND_LABEL[b]} (${BAND_HOURS[b]}h local) — ${v} event(s), click to see them`}
          arrow
        >
          <Box
            role="button"
            onClick={() => setDrill({ kind: "events", region: r, band: b })}
            sx={{
              cursor: "pointer",
              fontSize: 12,
              textAlign: "center",
              py: 0.75,
              color: alpha > 0.55 ? "#fff" : "text.primary",
              backgroundColor: rgba(GREEN, alpha),
              borderBottom: "1px solid",
              borderColor: "action.hover",
              "&:hover": { filter: "brightness(0.95)" },
            }}
          >
            {v}
          </Box>
        </Tooltip>,
      );
    });
    cells.push(
      <Box
        key={`m-${r.offset}`}
        role="button"
        onClick={() => setDrill({ kind: "members", region: r })}
        sx={{
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 700,
          textAlign: "right",
          px: 1,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "action.hover",
          "&:hover": { color: "primary.main" },
        }}
      >
        {fmtInt(r.members)}
      </Box>,
    );
  });

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
        <TextField
          select
          size="small"
          label="Window"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value={7}>Next 7 days</MenuItem>
          <MenuItem value={14}>Next 14 days</MenuItem>
          <MenuItem value={30}>Next 30 days</MenuItem>
        </TextField>
        <Box>
          <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
            Show events hosted by:
          </Typography>
          {(supply.hostTypes ?? []).map((h) => (
            <Chip
              key={h.key}
              label={h.label}
              size="small"
              color={hosts.has(h.key) ? "primary" : "default"}
              variant={hosts.has(h.key) ? "filled" : "outlined"}
              onClick={() => toggleHost(h.key)}
              sx={{ mr: 0.5 }}
            />
          ))}
        </Box>
      </Stack>

      <Box sx={{ overflowX: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: gridTemplate,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            minWidth: 680,
          }}
        >
          {cells}
        </Box>
      </Box>
      <Typography variant="caption" color="textSecondary" component="p" mt={1}>
        Rows are members grouped by <b>UTC offset</b> (so big countries split into east/west rows).
        Cells = how many of the <b>{fmtInt(shownEvents.length)}</b> shown events land in that part
        of a member’s local day; greener = more.
        <b> Click a cell</b> to see the events (and invite/share the region), or a Members count to
        see who’s there.
      </Typography>

      {drill && (
        <SupplyDrill drill={drill} shownEvents={shownEvents} onClose={() => setDrill(null)} />
      )}
    </Box>
  );
};

// ── Drill dialog (events in a band, or members in a region) ──────────────────
const SupplyDrill: React.FC<{
  drill: NonNullable<Drill>;
  shownEvents: TzEvent[];
  onClose: () => void;
}> = ({ drill, shownEvents, onClose }) => {
  const isMembers = drill.kind === "members";
  const region = drill.region;

  const { data, loading, error } = useQuery(ADMIN_TIMEZONE_REGION_MEMBERS, {
    variables: { offset: region.offset, limit: 500 },
    skip: !isMembers,
    fetchPolicy: "network-only",
  });
  const memberData: TimezoneRegionMembers | undefined = data?.adminTimezoneRegionMembers;

  const bandEvents =
    drill.kind === "events"
      ? shownEvents
          .filter((e) => bandOf(e.utcMin, region.offset) === drill.band)
          .sort((a, b) => a.startMs - b.startMs)
      : [];

  const offsetMinutes = Math.round(region.offset * 60);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}
      >
        <span>
          {region.label}
          {drill.kind === "events" ? ` · ${BAND_LABEL[drill.band]}` : " · members"}
        </span>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isMembers ? (
          loading && !memberData ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error.message}</Alert>
          ) : !memberData || memberData.members.length === 0 ? (
            <Typography color="textSecondary">No members found in this region.</Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Time zone</TableCell>
                    <TableCell>English level</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {memberData.members.map((m) => (
                    <TableRow key={`${m.name}-${m.country}-${m.timezone ?? ""}`} hover>
                      <TableCell>{m.name}</TableCell>
                      <TableCell>{m.country}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{m.timezone ?? "—"}</TableCell>
                      <TableCell>{m.level}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {memberData.truncated && (
                <Typography variant="caption" color="textSecondary" component="p" mt={1}>
                  Showing the first {fmtInt(memberData.members.length)} — there are more.
                </Typography>
              )}
            </>
          )
        ) : bandEvents.length === 0 ? (
          <Typography color="textSecondary">
            No events in this local time band for the selected host types.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Local date</TableCell>
                <TableCell>Local time</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Host</TableCell>
                <TableCell>Type</TableCell>
                <TableCell />
                <TableCell>Reach the region</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bandEvents.map((e) => {
                const lt = localDateTime(e.startMs, region.offset);
                return (
                  <TableRow key={e.eventId} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{lt.day}</TableCell>
                    <TableCell>{lt.time}</TableCell>
                    <TableCell>{e.title}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{e.host ?? "—"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {HOST_LABEL[e.hostType] ?? e.hostType}
                    </TableCell>
                    <TableCell>
                      <Link href={e.appLink} target="_blank" rel="noopener" variant="body2">
                        open ↗
                      </Link>
                    </TableCell>
                    <TableCell>
                      <RegionOutreach
                        eventId={e.eventId}
                        eventTitle={e.title}
                        offsetMinutes={offsetMinutes}
                        regionLabel={region.label}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EventTimezoneSupplyPanel;
